import { Link, useLocation } from 'react-router-dom';
import { Home, MessageCircle, User, ShoppingBag, LifeBuoy, Briefcase, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const TOURIST_TABS = [
  { id: 'home', icon: Home, label_en: 'Home', label_es: 'Inicio', to: '/dashboard' },
  { id: 'catalog', icon: ShoppingBag, label_en: 'Catalog', label_es: 'Catálogo', to: '/catalog' },
  { id: 'bookings', icon: Briefcase, label_en: 'Bookings', label_es: 'Reservas', to: '/bookings' },
  { id: 'chuy', icon: MessageCircle, label_en: 'Chuy', label_es: 'Chuy', to: '/chuy-chat' },
  { id: 'profile', icon: User, label_en: 'Profile', label_es: 'Perfil', to: '/profile' },
];

const PROVIDER_TABS = [
  { id: 'home', icon: Home, label_en: 'Home', label_es: 'Inicio', to: '/dashboard' },
  { id: 'catalog', icon: ShoppingBag, label_en: 'My Catalog', label_es: 'Mi Catálogo', to: '/provider/catalog' },
  { id: 'requests', icon: Sparkles, label_en: 'Searches', label_es: 'Solicitudes', to: '/provider/requests' },
  { id: 'bookings', icon: Briefcase, label_en: 'Bookings', label_es: 'Reservas', to: '/bookings' },
  { id: 'profile', icon: User, label_en: 'Profile', label_es: 'Perfil', to: '/profile' },
];

const GUEST_TABS = [
  { id: 'home', icon: Home, label_en: 'Home', label_es: 'Inicio', to: '/' },
  { id: 'catalog', icon: ShoppingBag, label_en: 'Catalog', label_es: 'Catálogo', to: '/catalog' },
  { id: 'login', icon: User, label_en: 'Sign in', label_es: 'Entrar', to: '/auth' },
  { id: 'chuy', icon: LifeBuoy, label_en: 'Chuy', label_es: 'Chuy', to: '/chuy-help' },
];

export const BottomNav = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();

  // Hide on auth page
  if (location.pathname === '/auth') return null;

  let tabs;
  if (!user) tabs = GUEST_TABS;
  else if (user.role === 'provider') tabs = PROVIDER_TABS;
  else tabs = TOURIST_TABS;

  const cols = tabs.length;

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full max-w-md bg-card/95 backdrop-blur-xl border-t border-border shadow-2xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className={`grid h-16`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          // Strip query string when comparing the active state
          const tabPath = tab.to.split('?')[0];
          const isActive =
            location.pathname === tabPath ||
            (tab.id === 'home' && location.pathname === '/dashboard');
          return (
            <Link
              key={tab.id}
              to={tab.to}
              data-testid={`bottom-nav-${tab.id}`}
              className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                isActive ? 'text-caribbean' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              <span className="leading-tight text-center">{language === 'en' ? tab.label_en : tab.label_es}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

/**
 * MobileShell: wraps page content into a mobile-first phone-frame container.
 * Use this on every page so the entire app looks like a native mobile app.
 */
export const MobileShell = ({ children, withBottomNav = true, className = '' }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/40 via-background to-muted/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div
        className={`relative mx-auto w-full max-w-md min-h-screen bg-background shadow-xl overflow-hidden ${className}`}
        style={{ paddingBottom: withBottomNav ? '7rem' : '0' }}
      >
        {children}
        {withBottomNav && <BottomNav />}
      </div>
    </div>
  );
};
